import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { Matched } from 'src/app/core/models/lead-campaign';
import { WindowScrollService } from 'src/app/core/services/window-scroll.service';
import { FeatureToggleService } from 'src/app/core/toggle/feature-toggle.service';
import { ButtonsPopupService } from 'src/app/shared/components/buttons-popup/service/buttons-popup.service';
import { SnackbarService } from 'src/app/shared/components/snackbar/service/snackbar.service';
import { LeadCampaignService } from '../lead-campaign.service';
import { SubscriptionStatusEnum } from '../enum/subscription-status-enum';

@Component({
  selector: 'app-application-summary',
  templateUrl: './application-summary.component.html',
  styleUrls: ['./application-summary.component.scss']
})
export class ApplicationSummaryComponent implements OnInit {

  @Output() public closeSummary = new EventEmitter<boolean>();
  @Output() public finished = new EventEmitter<boolean>();

  @Input() public campaign;
  @Input() public isSummary: boolean = false;
  
  public minValue: number = 500;
  public maxParticipants: number = 5;
  public messageErro: string = 'Ajuste os percentuais para totalizar 100%';
  public valueErro: string = 'O somatório dos participantes deve ser igual ao total investido';
  public participantFieldError = [];
  public formSummary: FormGroup;
  public isLoading: boolean = true;
  public isSegmentationAvailable: boolean;
  public isGeneratingBillet: boolean;
  public isEditable: boolean = true;
  public btnBilletLabel: string = 'Gerar boleto';
  public billetUrl: string;
  public btnLoading: boolean = true;
  public wasModified: boolean = false;

  constructor(
    private fb: FormBuilder,
    private scroll: WindowScrollService,
    private campaignService: LeadCampaignService,
    private snackService: SnackbarService,
    private featureToggleService: FeatureToggleService,
    private btnService: ButtonsPopupService
  ) { }

  ngOnInit(): void {
    this.initialize();
    this.checkToggles();
    this.loadSubSettings();
  }

  private checkToggles(): void {
    this.isGeneratingBillet = this.featureToggleService.isActive('toggle_lopes_lead_generate_billet');
    this.isSegmentationAvailable = this.featureToggleService.isActive('toggle_lopes_lead_segmentation_available');
  }

  private loadSubSettings() {
    const isCustomSub = this.featureToggleService.isActive('toggle_lopes_lead_custom_subscription');
    const group = this.campaign.campaign_group;
    if (isCustomSub && group.max_realtor) {
      this.minValue = group.min_value;
      this.maxParticipants = group.max_realtor;
    }
  }

  private initialize(): void {
    this.formSummary = this.fb.group({
      id: [null, Validators.required],
      productName: [this.campaign.product_name, Validators.required],
      productImageUrl: [this.campaign.product_image_url, Validators.required],
      sku: [this.campaign.product_sku, Validators.required],
      subscription: [this.campaign.subscription, Validators.required],
      duration: [this.campaign.duration, Validators.required],
      investmentAmount: [null, Validators.required],
      participants: [null, Validators.required],
      typeSegmentation: [{title: 'Prefiro que a Lopes escolha por mim.'}, Validators.required],
      campaignGroup: [this.campaign.campaign_group, Validators.required],
      subscriptionStatus: [null],
      subscriptionStatusLabel: [null]
    });
    this.getSubscriptionInfo();
  }
  
  public getSubscriptionInfo(): void {
    this.campaignService.getSubscription(this.campaign.id)
    .pipe(take(1))
    .subscribe(res => {
      const sub = res.body;
      const status = SubscriptionStatusEnum[sub.subscription_status];
      const participants = sub.participants?.map(participant => {
        let part = {...participant} //Cloning without reference
        part.participation_value = this.getValue(
          sub.investment_amount, 
          part.participation_percentage
        );
        return part;
      });

      this.formSummary.controls.participants = this.fb.array(participants);
      this.formSummary.patchValue({
        id: sub.id,
        investmentAmount: sub.investment_amount,
        typeSegmentation: sub.segmentation_type,
        subscriptionStatus: sub.subscription_status,
        subscriptionStatusLabel: status
      });

      this.getBillet();
    });
  }

  public getValue(amount: number, percent: number): number {
    return Number((amount / 100 * percent).toFixed(2));
  }

  get participants() {
    return this.formSummary.get('participants') as FormArray;
  }
  
  public minParticipantValue = () => {
    return {
      title: 'ATENÇÃO!',
      description: `O valor mínimo para essa campanha é de 
      ${this.minValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`,
      buttons: [
        {
          btnType: 0,
          title: 'OK',
          func: () => null,
        }
      ]
    }
  };

  public generateConfirmation = () => {
    return {
      title: 'ATENÇÃO!',
      description: `Após o boleto ser gerado, não será possível alterar os dados da sua inscrição.
      Deseja prosseguir?`,
      buttons: [
        {
          btnType: 3,
          title: 'Cancelar',
          func: () => null,
        },
        {
          btnType: 0,
          title: 'Confirmar',
          func: () => this.generateBillet(),
        }
      ]
    }
  };

  public changesNotSaved = () => {
    return {
      title: 'ATENÇÃO!',
      description: `Você possui mudanças que ainda não foram salvas, deseja 
      prosseguir mesmo assim?`,
      buttons: [
        {
          btnType: 3,
          title: 'Cancelar',
          func: () => null,
        },
        {
          btnType: 0,
          title: 'Confirmar',
          func: () => {
            this.wasModified = false;
            setTimeout(() => this.handleBilletGeneration(), 200)
          },
        }
      ]
    }
  };

  public closeModal(formChanged: boolean = false): void {
    this.closeSummary.emit(formChanged);
    this.scroll.disable();
  }

  public prepareData() {
    const values = this.formSummary.getRawValue();
    return {
      'id': values.id,
      'investment_amount': values.investmentAmount,
      'participants': values.participants
    }
  }

  public save(): void {
    this.isLoading = true;
    const total = this.formSummary.value.investmentAmount;
    if (total < this.minValue) {
      this.btnService.show(this.minParticipantValue());
      this.isLoading = false;
      return;
    }
    
    const preparedData = this.prepareData();
    
    this.campaignService.updateSubscription(
      this.campaign.id,
      preparedData
    ).pipe(take(1))
    .subscribe(res => {
      if (res.status_code_value === 200) {
        this.snackService.show('Inscrição atualizada com sucesso.', 'success');
        this.closeModal(true);
      }
    }, err => {
      this.snackService.show('Não foi possível atualizar as informações.', 'error', 10);
      throw new Error(err);
    }, () => {
      this.isLoading = false;
    });
  }

  public isFormInvalid(): boolean {
    const participants = this.formSummary.getRawValue().participants;
    return participants?.find(part => part.user_id === null);
  }

  public newParticipant(): void {
    if (this.participants.length >= this.maxParticipants) {
      return;
    }
    this.participants.push(this.addUserAutocomple());
  }

  public addUserAutocomple(data?: Matched, value?: number): FormGroup {
    return this.fb.group({
      user_id: [data?.id ? data.id : null, Validators.required],
      user_name: [data?.name ? data.name : null, Validators.required],
      user_nickname: [data?.nickname ? data.nickname : null, Validators.required],
      participation_value: [value, Validators.required],
      participation_percentage: [value, Validators.required],
      picture_url: [''],
    });
  }

  public editParticipant(data: any): void {
    const { index, participant } = data;
    let totalSum = this.getTotalInvestiment(index);
    let participantValue = participant.participation_value;

    if (typeof participantValue === 'string') {
      participantValue = this.campaignService.parseCurrency(participant.participation_value);
    }

    totalSum += participantValue;

    let newPartArray = this.participants.value.map((part, ind) => {
      let partValue = part.participation_value;

      if (ind === index) {
        part.user_id = participant.user_id;
        part.picture_url = participant.picture_url;
        part.user_name = participant.user_name;
        part.user_nickname = participant.user_nickname;
        part.participation_value = participantValue;
        partValue = participantValue;
      }

      let partPercentage = this.campaignService.getPercentage(totalSum, partValue);
      part.participation_percentage = partPercentage;

      return part;
    });

    this.wasModified = true;
    this.formSummary.controls.investmentAmount.setValue(totalSum);
    this.formSummary.controls.participants = this.fb.array(newPartArray);
  }

  public getTotalInvestiment(ignoreIndex: number = null): number {
    return this.formSummary.controls.participants.value.reduce((acc, part, ind) => {
      if (ind !== ignoreIndex) {
        acc += part.participation_value;
      }
      return acc;
    }, 0);
  }

  public removeParticipant(index: number):void {
    const totalSum = this.getTotalInvestiment(index);
    this.participants.removeAt(index);
    
    const participants = this.participants.getRawValue().map(participant => {
      let part = {...participant} //Cloning without reference
      part.participation_percentage = this.campaignService.getPercentage(
        totalSum, part.participation_value
      );
      
      return part;
    });
    
    this.formSummary.controls.participants = this.fb.array(participants);
    this.formSummary.controls.investmentAmount.setValue(totalSum);
  }

  public handleBilletGeneration(): void {
    if (this.wasModified) { 
      this.btnService.show(this.changesNotSaved());
      return;
    }
    if (this.isEditable) {
      this.btnService.show(this.generateConfirmation());
      return;
    }
    window.open(this.billetUrl);
  }

  public generateBillet(): void {
    this.btnLoading = true;

    this.campaignService.generateBillet(
      this.campaign.id,
      this.formSummary.controls.id.value
    ).pipe(take(1))
    .subscribe(res => {
      if (res.status_code_value === 200) {
        setTimeout(() => {
          this.snackService.show('Boleto solicitado! Estará disponível para download em alguns instantes.', 'success');
          this.getSubscriptionInfo();
        }, 3000)
      }
    }, err => {
      this.snackService.show('Não foi possível atualizar as informações.', 'error', 10);
      this.btnLoading = false;
      throw new Error(err.message);
    });
  }

  public getBillet() {
    this.btnLoading = true;
    
    this.campaignService.getBillet(
      this.formSummary.controls.id.value
    ).pipe(take(1))
    .subscribe(res => {
      if (res.file_url) {
        this.billetUrl = res.file_url;
        this.btnBilletLabel = 'Visualizar boleto';
        this.btnLoading = false;
      }
      if (this.formSummary.get('subscriptionStatus').value != 'PAYMENT_PENDING') {
        this.isEditable = false;
      }
    }, err => {
      this.snackService.show('Não foi possível buscar o boleto..', 'error', 10);
      throw new Error(err.message);
    }, () => {
      this.btnLoading = false;
      this.isLoading = false;
    });
  }

  public showBilletBtn(): boolean {
    const status = ['PAYMENT_PENDING', 'INVOICE_GENERATED'];
    
    return this.isGeneratingBillet && 
    status.includes(this.formSummary.get('subscriptionStatus').value);
  }
}